import React, {useState, useEffect, Fragment} from "react";
import {useDispatch, useSelector} from "react-redux";
import ReactHtmlParser from "react-html-parser";
// import WebViewer from '@pdftron/webviewer'

// Material-UI Core
import {makeStyles, withStyles} from "@material-ui/core/styles";
import MuiAccordion from "@material-ui/core/Accordion";
import MuiAccordionSummary from "@material-ui/core/AccordionSummary";
import MuiAccordionDetails from "@material-ui/core/AccordionDetails";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";
import Paper from "@material-ui/core/Paper";
import Tooltip from "@material-ui/core/Tooltip";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import CardContent from "@material-ui/core/CardContent";
import Avatar from "@material-ui/core/Avatar";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Fab from "@material-ui/core/Fab";
import Popover from "@material-ui/core/Popover";
import Badge from "@material-ui/core/Badge";

// Material-UI Lab
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import Pagination from "@material-ui/lab/Pagination";

// Material-UI Icon
import Button from "@material-ui/core/Button";
import MailIcon from "@material-ui/icons/Mail";
import DraftsIcon from "@material-ui/icons/Drafts";
import ReplyIcon from "@material-ui/icons/Reply";
import ForwardIcon from "@material-ui/icons/Forward";
import RefreshIcon from "@material-ui/icons/Refresh";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import RestoreIcon from "@material-ui/icons/Restore";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";

// Custom Component
import {Alert} from "../Alert/Alert";
import ConfirmDialog from "../Dialog/ConfirmDialog";
import FullScreenDialog from "../Dialog/FullScreenDialog";
import useLoading from "../Loading/useLoading";
import Legend from "../Legend/Legend";
import AttachmentChip from "../Chip/AttachmentChip";
import CirculationAttachmentChip from "../Chip/CirculationAttachmentChip";
import RecipientChip from "../Chip/RecipientChip";
import FilePreview from "../FilePreview/FilePreview";
import CirculationFilePreview from "../FilePreview/CirculationFilePreview";
import CirculationSearchForm from "./CirculationSearchForm";
import CirculationCreateForm from "./CirculationCreateForm";
import ReadingStatusForm from "./ReadingStatusForm";
import CirculationReplyForm from "./CirculationReplyForm";
import CirculationForwardForm from "./CirculationForwardForm";
import Efiling from "../../views/Efiling/Efiling.js";
import LockScreenLoading from "../LockScreenLoading/LockScreenLoading";

// Custom Style
import {circulationStyle} from "../../styles/jss/components/circulation/circulationStyle";
import {
    redColor,
    oceanBlueColor,
    grayColor,
    blackColor,
} from "../../styles/jss/cmsMainStyle";

// Global Function
import {avatarNameColor} from "../../global/avatarColor";
import {getFile} from "../../global/getPropertyInfo";
import {webViewerSaveAnnotation} from "../../global/webViewerSaveAnnotation";

// Fetch Data
import {
    getCirculation,
    searchCirculation,
    getCirculationDetail,
    setCirculationReaded,
    recallCirculation,
} from "../../dataSource/circulation";
import {circulationFileDownload} from "../../dataSource/fileManager";

// Custom Style (Typography)
const InboxTypography = withStyles({
    root: {
        fontSize: 20,
        fontWeight: 600,
        color: grayColor[6],
    },
})(Typography);

const CircCodeTypography = withStyles({
    root: {
        fontSize: 18,
        fontWeight: 600,
        color: oceanBlueColor[11],
    },
})(Typography);

const CircCodeRecallTypography = withStyles({
    root: {
        fontSize: 18,
        fontWeight: 600,
        color: "#c32d2d",
    },
})(Typography);

const CircSubjectTypography = withStyles({
    root: {
        fontSize: 15,
        fontWeight: 400,
        color: grayColor[6],
    },
})(Typography);

const CircSendInfoTypography = withStyles({
    root: {
        fontSize: 15,
        fontWeight: 400,
        color: oceanBlueColor[12],
    },
})(Typography);

const MsgTitleTypography = withStyles({
    root: {
        fontSize: 15,
        fontWeight: 600,
        color: grayColor[14],
    },
})(Typography);

const MsgContentTypography = withStyles({
    root: {
        fontSize: 15,
        fontWeight: 400,
        color: blackColor,
    },
})(Typography);

const ListMsgTypography = withStyles({
    root: {
        fontSize: 15,
        fontWeight: 600,
        color: "red",
    },
})(Typography);

const PopoverMemberNameTypography = withStyles({
    root: {
        fontSize: 15,
        fontWeight: 400,
        color: oceanBlueColor[10],
    },
})(Typography);

const PopoverCompanyNameTypography = withStyles({
    root: {
        fontSize: 12,
        fontWeight: 400,
        color: grayColor[5],
    },
})(Typography);

// Custom Style (Accordion)
const Accordion = withStyles({
    root: {
        border: "1px solid rgba(0, 0, 0, .125)",
        boxShadow: "none",
        "&:not(:last-child)": {
            borderBottom: 0,
        },
        "&:before": {
            display: "none",
        },
        "&$expanded": {
            margin: "auto",
        },
    },
    expanded: {},
})(MuiAccordion);

const AccordionSummary = withStyles({
    root: {
        backgroundColor: "rgba(0, 0, 0, .03)",
        borderBottom: "1px solid rgba(0, 0, 0, .125)",
        marginBottom: -1,
        minHeight: 56,
        "&$expanded": {
            minHeight: 56,
        },
    },
    content: {
        "&$expanded": {
            margin: "12px 0",
        },
    },
    expanded: {},
})(MuiAccordionSummary);

const AccordionDetails = withStyles((theme) => ({
    root: {
        padding: theme.spacing(1),
        // cursor: "pointer"
    },
}))(MuiAccordionDetails);

// Custom CardContent
const CircCardContent = withStyles((theme) => ({
    root: {
        paddingTop: theme.spacing(0),
        paddingBottom: theme.spacing(0),
    },
}))(CardContent);

// Custom Badge
const CirculationBadge = withStyles((theme) => ({
    badge: {
        right: -20,
        top: 12,

        //   border: `2px solid ${theme.palette.background.paper}`,
        //   padding: '0 4px',
    },
}))(Badge);

const useStyles = makeStyles((theme) => circulationStyle(theme));

const store = require("store");

const Circulation = (props) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const {} = props;

    // Redux Data
    // const circulationListRedux = useSelector(state => state.circulationListRedux)
    // const circulationDetailRedux = useSelector(state => state.circulationDetailRedux)
    const propertySeqRedux = useSelector((state) => state.propertySeqRedux);
    const userModulesRedux = useSelector((state) => state.userModulesRedux);

    // Loading
    const [getCircListLoading, getCircListShowLoading, getCircListHiddenLoading] =
        useLoading();
    const [
        getCircSearchLoading,
        getCircSearchShowLoading,
        getCircSearchHiddenLoading,
    ] = useLoading();
    const [
        getCircContentLoading,
        getCircContentShowLoading,
        getCircContentHiddenLoading,
    ] = useLoading();

    const [lockScreenLoadingOpen, setLockScreenLoadingOpen] = useState(false);
    const [progressTitle, setProgressTitle] = useState("Sending");

    const [expanded, setExpanded] = useState("panel1");
    const [circulationList, setCirculationList] = useState(null);
    const [circulationDetail, setCirculationDetail] = useState(null);
    const [filePreviewProperty, SetFilePreviewProperty] = useState(null);
    const [propertyFiles, setPropertyFiles] = useState(null);
    const [propertySubject, setPropertySubject] = useState(null);
    const [filePreviewDialogOpen, setFilePreviewDialogOpen] = useState(false);
    const [newCirculationDialogOpen, setNewCirculationDialogOpen] =
        useState(false);
    const [readingStatusDialogOpen, setReadingStatusDialogOpen] = useState(false);
    const [replyCirculationDialogOpen, setReplyCirculationDialogOpen] =
        useState(false);
    const [forwardCirculationDialogOpen, setForwardCirculationDialogOpen] =
        useState(false);
    const [fileManagerDialogOpen, setFileManagerDialogOpen] = useState(false);
    const [renewCircList, setRenewCircList] = useState(0);
    const [modifyRight, setModifyRight] = useState(false);
    const [listFilter, setListFilter] = useState("ALL");
    const [openPopoverId, setOpenPopoverId] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [listMsg, setListMsg] = useState("");
    const [loadCirculationContent, setLoadCirculationContent] = useState(false);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [fullList, setFullList] = useState(null);
    const [readList, setReadList] = useState(null);
    const [unreadList, setUnreadList] = useState(null);
    const [recallList, setRecallList] = useState(null);
    const [fullListCount, setFullListCount] = useState(0);
    const [readListCount, setReadListCount] = useState(0);
    const [unreadListCount, setUnreadListCount] = useState(0);
    const [recallListCount, setRecallListCount] = useState(0);
    const [circulationSeq, setCirculationSeq] = useState(null);
    const [searchParameter, setSearchParameter] = useState({
        code: "",
        sname: "",
        subject: "",
        sdfrom: null,
        sdto: null,
    });

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertData, setAlertData] = useState({
        type: "",
        title: "",
        content: "",
    });

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmDialogData, setConfirmDialogData] = useState({
        dialogTitle: "",
        dialogContent: "",
        confirmAction: "",
        cancelAction: "",
    });

    const handleRecipientClipClick = (popoverId) => (event) => {
        setOpenPopoverId(popoverId);
        setAnchorEl(event.currentTarget);
    };

    const handleRecipientClipClose = () => {
        setOpenPopoverId(null);
        setAnchorEl(null);
    };

    const handleMenuChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Build Alert
    const buildAlert = (data) => {
        setAlertData({
            type: data.type,
            title: data.title,
            content: data.content,
        });
        setAlertOpen(true);
    };

    // Confirm dialog
    const confirmDialogHandleOpen = (data) => {
        setConfirmDialogData({
            dialogTitle: data.dialogTitle,
            dialogContent: data.dialogContent,
            confirmAction: data.confirmAction,
            cancelAction: data.cancelAction,
        });

        setConfirmDialogOpen(true);
    };

    const confirmDialogHandleClose = () => {
        setConfirmDialogOpen(false);
    };

    const recallDialogHandleOpen = () => {
        confirmDialogHandleOpen({
            dialogTitle: "Circulation Recall",
            dialogContent: "Are you sure to recall the circulation.",
            confirmAction: recallSaveAction,
            cancelAction: recallActionHandleClose,
        });
    };

    const recallSaveAction = async () => {
        try {
            await recallCirculation(circulationDetail.circ_seq);
            setRenewCircList((prev) => prev + 1);
            buildAlert({
                // Alert Type: success, info, warning, error
                type: "success",
                title: "Success Alert",
                content: `Recall Circulation successfully`,
            });
            setConfirmDialogOpen(false);
        } catch (err) {
            buildAlert({
                // Alert Type: success, info, warning, error
                type: "error",
                title: "Error Alert",
                content: err.message,
            });
        }
    };

    const recallActionHandleClose = () => {
        setConfirmDialogOpen(false);
    };

    // Dialog open and close
    const newCirculationDialogHandleOpen = () => {
        setNewCirculationDialogOpen(true);
    };

    const newCirculationDialogHandleClose = () => {
        resetCirculationData();
        setNewCirculationDialogOpen(false);
    };

    const readingStatusDialogHandleOpen = () => {
        setReadingStatusDialogOpen(true);
    };

    const readingStatusDialogHandleClose = () => {
        setReadingStatusDialogOpen(false);
    };

    const replyCirculationDialogHandleOpen = () => {
        setReplyCirculationDialogOpen(true);
    };

    const replyCirculationDialogHandleClose = () => {
        resetCirculationData();
        setReplyCirculationDialogOpen(false);
    };

    const forwardCirculationDialogHandleOpen = () => {
        setForwardCirculationDialogOpen(true);
    };

    const forwardCirculationDialogHandleClose = () => {
        resetCirculationData();
        setForwardCirculationDialogOpen(false);
    };

    const filePreviewDialogHandleOpen = () => {
        setFilePreviewDialogOpen(true);
    };

    const filePreviewDialogHandleClose = () => {
        setFilePreviewDialogOpen(false);
    };

    const fileManagerDialogHandleOpen = () => {
        setFileManagerDialogOpen(true);
    };

    const fileManagerDialogHandleClose = () => {
        clearFileChoice();
        setFileManagerDialogOpen(false);
    };

    const annotationSaveActionHandleOpen = () => {
        confirmDialogHandleOpen({
            dialogTitle: "Annotation Save",
            dialogContent: "Are you sure to save the annotation.",
            confirmAction: annotationSaveAction,
            cancelAction: annotationSaveActionHandleClose,
        });
    };

    const annotationSaveActionHandleClose = () => {
        confirmDialogHandleClose();
    };

    const annotationSaveAction = () => {
        webViewerSaveAnnotation(
            filePreviewProperty.fileCategory,
            filePreviewProperty.fileSequence
        ).then((res) => {
            annotationSaveActionHandleClose();

            if (res.type === "success") {
                buildAlert({
                    // Alert Type: success, info, warning, error
                    type: "success",
                    title: "Success Alert",
                    content: "The Annotation have been save",
                });
            } else {
                buildAlert({
                    // Alert Type: success, info, warning, error
                    type: "error",
                    title: "Error Alert",
                    content: res.message,
                });
            }
        });
    };

    // Main Action
    const getCircListAction = async (searchKey, searching = false) => {
        if (searching) {
            getCircSearchShowLoading();
        }

        searchCirculation(searchKey).then((res) => {
            let tempReadList = [];
            let tempUnreadList = [];
            let tempRecallList = [];

            res.data.map((record) => {
                if (record.is_recalled) {
                    tempRecallList = [...tempRecallList, record];
                } else if (record.is_read) {
                    tempReadList = [...tempReadList, record];
                } else {
                    tempUnreadList = [...tempUnreadList, record];
                }
            });

            setFullList(res.data);
            setReadList(tempReadList);
            setUnreadList(tempUnreadList);
            setRecallList(tempRecallList);
            setFullListCount(res.data.length);
            setReadListCount(tempReadList.length);
            setUnreadListCount(tempUnreadList.length);
            setRecallListCount(tempRecallList.length);

            if (listFilter === "ALL") {
                setCirculationList(res.data);
            } else if (listFilter === "READ") {
                setCirculationList(tempReadList);
            } else if (listFilter === "UNREAD") {
                setCirculationList(tempUnreadList);
            } else if (listFilter === "RECALLED") {
                setCirculationList(tempRecallList);
            } else {
                setCirculationList(null);
            }

            // setCirculationList(res.data)
            dispatch({
                type: "ADD_CIRCULATION_LIST",
                data: res.data,
            });

            if (res.data.length === 0) {
                setListMsg("No Circulation");
            } else {
                setListMsg("");
            }
            if (searching) {
                getCircSearchHiddenLoading();
            }

            getCircListHiddenLoading();
        });
    };
    const getCircDetailAction = async (circSeq, isRead) => {
        getCircContentShowLoading();
        setLoadCirculationContent(true);
        setCirculationSeq(circSeq);

        await setCircReadedAction(circSeq, isRead);

        getCirculationDetail(circSeq).then((res) => {
            setCirculationDetail(res.data[0]);
            dispatch({
                type: "ADD_CIRCULATION_DETAIL",
                data: res.data[0],
            });

            getCircContentHiddenLoading();
            setLoadCirculationContent(false);
        });
    };

    const setCircReadedAction = async (circSeq, isRead) => {
        if (!isRead) {
            setCirculationReaded(circSeq).then((res) => {
                getCircListAction(searchParameter);

                console.log("*******************************************************");
                console.log(`Set Circulation to readed (Circulation Seq: ${circSeq})`);
                console.log(res);
                console.log("*******************************************************");
            });
        }
    };

    const viewFileAction = (
        circulationSeq,
        CirculationMessageSeq,
        messageFileSeq,
        filename
    ) => {
        SetFilePreviewProperty({
            circulationSeq: circulationSeq,
            CirculationMessageSeq: CirculationMessageSeq,
            messageFileSeq: messageFileSeq,
            filename: filename,
        });

        filePreviewDialogHandleOpen();
    };

    const downloadFileAction = async (
        circulationSeq,
        CirculationMessageSeq,
        messageFileSeq
    ) => {
        try {
            await circulationFileDownload(
                circulationSeq,
                CirculationMessageSeq,
                messageFileSeq
            ).then((res) => {
                console.log(
                    "*****************************************************************************"
                );
                console.log("System Module: Document Submission");
                console.log("Message: File download successful.");
                console.log(
                    "*****************************************************************************"
                );
            });
        } catch (err) {
            buildAlert({
                // Alert Type: success, info, warning, error
                type: "error",
                title: "Error Alert",
                content: "Cannot Download the Attachment",
            });
        }
    };

    const resetCirculationData = () => {
        setPropertyFiles(null);
        dispatch({
            type: "CREATE_CIRCULATION_DATA",
            data: null,
        });
    };

    // Dialog Button Function
    const fileManagerFileSelected = async () => {
        if (propertySeqRedux === null) {
            buildAlert({
                // Alert Type: success, info, warning, error
                type: "warning",
                title: "Warning Alert",
                content: "Not any file choice, Please choice the file first.",
            });

            return;
        }

        if (propertySeqRedux.length === 0) {
            buildAlert({
                // Alert Type: success, info, warning, error
                type: "warning",
                title: "Warning Alert",
                content: "Not any file choice, Please choice the file first.",
            });

            return;
        }

        setPropertyFiles(await getFile(propertySeqRedux));
        fileManagerDialogHandleClose();
    };

    // Clear Data
    const clearFileChoice = () => {
        dispatch({
            type: "FILE_SELECT",
            data: null,
        });
    };

    // Dialog Button
    const fileManagerDialogBtn = [
        {
            buttonName: "OK",
            buttonFunction: fileManagerFileSelected,
            chooseFile: false,
        },
    ];

    const handleListFilter = (event, newListFilter) => {
        setCirculationDetail(null);
        setPage(1);
        setListFilter(newListFilter);

        if (newListFilter === "ALL") {
            setCirculationList(fullList);
        } else if (newListFilter === "READ") {
            setCirculationList(readList);
        } else if (newListFilter === "UNREAD") {
            setCirculationList(unreadList);
        } else if (newListFilter === "RECALLED") {
            setCirculationList(recallList);
        } else {
            setListFilter("ALL");
            setCirculationList(fullList);
        }
    };

    useEffect(() => {
        if (userModulesRedux !== null) {
            userModulesRedux.map((module) => {
                if (module.path === "/circulation") {
                    if (module.rights.modifiable || module.rights.administrable) {
                        setModifyRight(true);
                    }
                }
            });
        }
    });

    useEffect(() => {
        const fetchCircList = async () => {
            getCircListShowLoading();
            await getCircListAction(searchParameter);
        };

        fetchCircList();
    }, [renewCircList]);

    return (
        console.log(
            "Circulation Detail Information | Variable: circulationDetail (Circulation.js)"
        ),
            console.log(circulationDetail),
            console.log(
                "File Preview | Variable: filePreviewProperty (Circulation.js)"
            ),
            console.log(filePreviewProperty),
            console.log(
                "---------------------------------------------------------------------------------"
            ),
            (
                <Fragment>
                    {/* <LockScreenLoading
          open={lockScreenLoadingOpen}
          circleSize={200}
          progressTitle={progressTitle}
        /> */}
                    <Alert
                        open={alertOpen}
                        alertType={alertData.type}
                        alertTitle={alertData.title}
                        alertContent={alertData.content}
                        handleClose={() => setAlertOpen(false)}
                    />

                    <ConfirmDialog
                        open={confirmDialogOpen}
                        dialogTitle={confirmDialogData.dialogTitle}
                        dialogContent={confirmDialogData.dialogContent}
                        confirmAction={confirmDialogData.confirmAction}
                        cancelAction={confirmDialogData.cancelAction}
                    />

                    <FullScreenDialog
                        open={fileManagerDialogOpen}
                        dialogTitle={"File Choice From eFiling"}
                        formContent={
                            <Efiling fileManagerType={"select"}/> // main => Main File Manager, select => Select File From File Manager
                        }
                        button={fileManagerDialogBtn}
                        handleClose={fileManagerDialogHandleClose}
                    />

                    <FullScreenDialog
                        open={newCirculationDialogOpen}
                        dialogTitle={"New Circulation"}
                        formContent={
                            <CirculationCreateForm
                                presetSubject={propertySubject}
                                presetFiles={propertyFiles}
                                setLockScreenLoadingOpen={setLockScreenLoadingOpen}
                                // setPresetFiles={setPropertyFiles}
                                setResetFileSelect={null}
                                openFileManagerDialog={fileManagerDialogHandleOpen}
                                closeCirculationDialog={newCirculationDialogHandleClose}
                                renewCircList={renewCircList}
                                setRenewCircList={setRenewCircList}
                                buildAlert={buildAlert}
                            />
                        }
                        button={[]}
                        handleClose={newCirculationDialogHandleClose}
                    />

                    <FullScreenDialog
                        open={readingStatusDialogOpen}
                        dialogTitle={"Reading Status"}
                        formContent={<ReadingStatusForm circulationSeq={circulationSeq}/>}
                        button={[]}
                        handleClose={readingStatusDialogHandleClose}
                    />

                    <FullScreenDialog
                        open={replyCirculationDialogOpen}
                        dialogTitle={"Reply Circulation"}
                        formContent={
                            <CirculationReplyForm
                                presetSubject={propertySubject}
                                presetFiles={propertyFiles}
                                setLockScreenLoadingOpen={setLockScreenLoadingOpen}
                                setResetFileSelect={null}
                                setLockScreenLoadingOpen={setLockScreenLoadingOpen}
                                openFileManagerDialog={fileManagerDialogHandleOpen}
                                circulationDetail={circulationDetail}
                                setCirculationDetail={setCirculationDetail}
                                closeCirculationDialog={replyCirculationDialogHandleClose}
                                renewCircList={renewCircList}
                                setRenewCircList={setRenewCircList}
                                buildAlert={buildAlert}
                            />
                        }
                        button={[]}
                        handleClose={replyCirculationDialogHandleClose}
                    />

                    <FullScreenDialog
                        open={forwardCirculationDialogOpen}
                        dialogTitle={"Forward Circulation"}
                        formContent={
                            <CirculationForwardForm
                                presetSubject={propertySubject}
                                setLockScreenLoadingOpen={setLockScreenLoadingOpen}
                                presetFiles={propertyFiles}
                                setResetFileSelect={null}
                                setLockScreenLoadingOpen={setLockScreenLoadingOpen}
                                openFileManagerDialog={fileManagerDialogHandleOpen}
                                circulationSeq={
                                    circulationDetail !== null ? circulationDetail.circ_seq : null
                                }
                                closeCirculationDialog={forwardCirculationDialogHandleClose}
                                renewCircList={renewCircList}
                                setRenewCircList={setRenewCircList}
                                buildAlert={buildAlert}
                            />
                        }
                        button={[]}
                        handleClose={forwardCirculationDialogHandleClose}
                    />

                    <FullScreenDialog
                        open={filePreviewDialogOpen}
                        dialogTitle={"Circuration Attachment File Preview"}
                        formContent={
                            <CirculationFilePreview
                                filePreviewProperty={filePreviewProperty}
                                // annotationChangeAlert={annotationChangeAlert}
                                // setAnnotationChangeAlert={setAnnotationChangeAlert}
                            />
                        }
                        button={[]}
                        handleClose={filePreviewDialogHandleClose}
                        // annotationChangeAlert={annotationChangeAlert}
                    />

                    <Grid container spacing={1}>
                        <Grid item sm={12} lg={4} md={6}>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={!modifyRight}
                                onClick={newCirculationDialogHandleOpen}
                                className={classes.circulationNewBtn}
                            >
                                New Circulation
                            </Button>
                            <Grid item lg={12} md={12}>
                                <ToggleButtonGroup
                                    value={listFilter}
                                    exclusive
                                    onChange={handleListFilter}
                                    aria-label="text alignment"
                                    className={classes.filterButtonGroup}
                                >
                                    <ToggleButton
                                        value="ALL"
                                        aria-label="show all"
                                        className={classes.filterButton}
                                    >
                                        <CirculationBadge
                                            showZero
                                            badgeContent={fullListCount}
                                            classes={{badge: classes.circulationBadge}}
                                        >
                                            <div>All</div>
                                        </CirculationBadge>
                                    </ToggleButton>

                                    <ToggleButton
                                        value="READ"
                                        aria-label="show read"
                                        className={classes.filterButton}
                                    >
                                        <CirculationBadge
                                            showZero
                                            badgeContent={readListCount}
                                            classes={{badge: classes.circulationBadge}}
                                        >
                                            <div>Read</div>
                                        </CirculationBadge>
                                    </ToggleButton>

                                    <ToggleButton
                                        value="UNREAD"
                                        aria-label="show unread"
                                        className={classes.filterButton}
                                    >
                                        <CirculationBadge
                                            showZero
                                            badgeContent={unreadListCount}
                                            classes={{badge: classes.circulationBadge}}
                                        >
                                            <div>Unread</div>
                                        </CirculationBadge>
                                    </ToggleButton>
                                    <ToggleButton
                                        value="RECALLED"
                                        aria-label="show recalled"
                                        className={classes.filterButton}
                                    >
                                        <CirculationBadge
                                            showZero
                                            badgeContent={recallListCount}
                                            classes={{badge: classes.circulationBadge}}
                                        >
                                            <div>Recalled</div>
                                        </CirculationBadge>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>
                            <Accordion>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon/>}
                                    aria-controls="panel4bh-content"
                                    id="panel4bh-header"
                                >
                                    <InboxTypography>SEARCH</InboxTypography>
                                </AccordionSummary>

                                <AccordionDetails>
                                    <CirculationSearchForm
                                        searchAction={getCircListAction}
                                        searchParameter={searchParameter}
                                        setSearchParameter={setSearchParameter}
                                        resetContent={setCirculationDetail}
                                        searchLoading={getCircSearchLoading}
                                    />
                                </AccordionDetails>
                            </Accordion>

                            <Accordion
                                square
                                expanded={expanded === "panel1"}
                                onChange={handleMenuChange("panel1")}
                            >
                                {/* <Accordion expanded={true}> */}
                                <AccordionSummary
                                    aria-controls="panel1d-content"
                                    id="panel1d-header"
                                >
                                    <InboxTypography>INBOX</InboxTypography>
                                    {/* <div><RefreshIcon onClick={refreshCirculationList} /></div> */}
                                    {/* <RefreshIcon onClick={refreshCirculationList}></RefreshIcon> */}
                                </AccordionSummary>

                                <div className={classes.circulationList}>
                                    {getCircListLoading}
                                    {circulationList !== null ? (
                                        circulationList.length > 0 ? (
                                            circulationList
                                                .slice(
                                                    (page - 1) * rowsPerPage,
                                                    (page - 1) * rowsPerPage + rowsPerPage
                                                )
                                                .map((res) => {
                                                    return (
                                                        <AccordionDetails
                                                            key={res.circ_seq}
                                                            onClick={() =>
                                                                getCircDetailAction(res.circ_seq, res.is_read)
                                                            }
                                                        >
                                                            <Card className={classes.circulationCard}>
                                                                <CardHeader
                                                                    avatar={
                                                                        <Avatar
                                                                            aria-label="recipe"
                                                                            className={classes.avatar}
                                                                            style={{
                                                                                "--avatar-color": avatarNameColor(
                                                                                    res.last_reply_sender.substring(0, 1)
                                                                                ),
                                                                            }}
                                                                        >
                                                                            {res.last_reply_sender.substring(0, 1)}
                                                                        </Avatar>
                                                                    }
                                                                    action={
                                                                        res.is_read ? (
                                                                            <Tooltip title="Readed Message">
                                                                                <DraftsIcon
                                                                                    className={classes.readedIcon}
                                                                                />
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <Tooltip title="Unread Message">
                                                                                <MailIcon
                                                                                    className={classes.unreadIcon}
                                                                                />
                                                                            </Tooltip>
                                                                        )
                                                                    }
                                                                    title={
                                                                        <CircSendInfoTypography variant="body1">
                                                                            {res.last_reply_sender}
                                                                        </CircSendInfoTypography>
                                                                    }
                                                                    subheader={
                                                                        <CircSendInfoTypography variant="body1">
                                                                            {res.last_reply_date}
                                                                        </CircSendInfoTypography>
                                                                    }
                                                                />

                                                                <CircCardContent>
                                                                    <CircCodeTypography variant="body1">
                                                                        {res.circ_code}
                                                                        {res.is_recalled ? "[RECALLED]" : ""}
                                                                    </CircCodeTypography>
                                                                    {/* <CircSubjectTypography variant="body1" noWrap={true}>{res.circ_subject}</CircSubjectTypography> */}

                                                                    <div className={classes.listSubject}>
                                                                        <CircSubjectTypography
                                                                            variant="body1"
                                                                            noWrap={true}
                                                                        >
                                                                            {res.circ_subject}
                                                                        </CircSubjectTypography>
                                                                    </div>
                                                                </CircCardContent>
                                                            </Card>
                                                        </AccordionDetails>
                                                    );
                                                })
                                        ) : (
                                            <div className={classes.listMsg}>
                                                <ListMsgTypography>{listMsg}</ListMsgTypography>
                                            </div>
                                        )
                                    ) : (
                                        ""
                                    )}
                                </div>
                                <Pagination
                                    count={
                                        circulationList && circulationList.length > 0
                                            ? Math.ceil(circulationList.length / rowsPerPage)
                                            : 0
                                    }
                                    variant="outlined"
                                    shape="rounded"
                                    onChange={handleChangePage}
                                    page={page}
                                    className={classes.circulationPagination}
                                />
                            </Accordion>
                        </Grid>

                        <Grid item sm={12} lg={8} md={6}>
                            {getCircContentLoading}
                            {!loadCirculationContent ? (
                                circulationDetail !== null ? (
                                    <Fragment>
                                        <List>
                                            <ListItem>
                                                <ListItemText
                                                    primary={
                                                        circulationDetail.is_recalled ? (
                                                            <CircCodeRecallTypography>
                                                                {circulationDetail.circ_code}[Recalled]
                                                            </CircCodeRecallTypography>
                                                        ) : (
                                                            <CircCodeTypography>
                                                                {circulationDetail.circ_code}
                                                            </CircCodeTypography>
                                                        )
                                                    }
                                                    secondary={
                                                        <MsgTitleTypography component={"p"}>
                                                            {circulationDetail.recall_date &&
                                                                `Recall Date: ${circulationDetail.recall_date}`}
                                                            {circulationDetail.recall_date && <br/>}
                                                            {circulationDetail.circ_subject}
                                                        </MsgTitleTypography>
                                                    }
                                                />

                                                <ListItemSecondaryAction>
                                                    {circulationDetail.is_recallable && (
                                                        <Tooltip title="Recall">
                                                            <Fab
                                                                color="primary"
                                                                size="small"
                                                                aria-label="reply"
                                                                className={classes.messageBtn}
                                                                onClick={recallDialogHandleOpen}
                                                            >
                                                                <RestoreIcon/>
                                                            </Fab>
                                                        </Tooltip>
                                                    )}
                                                    {!circulationDetail.is_recalled && (
                                                        <>
                                                            <Tooltip title="Reading Status">
                                                                <Fab
                                                                    color="primary"
                                                                    size="small"
                                                                    aria-label="reply"
                                                                    className={classes.messageBtn}
                                                                    onClick={readingStatusDialogHandleOpen}
                                                                >
                                                                    <AssignmentTurnedInIcon/>
                                                                </Fab>
                                                            </Tooltip>

                                                            <Tooltip title="Reply">
                                                                <Fab
                                                                    color="primary"
                                                                    size="small"
                                                                    aria-label="reply"
                                                                    className={classes.messageBtn}
                                                                    onClick={replyCirculationDialogHandleOpen}
                                                                >
                                                                    <ReplyIcon/>
                                                                </Fab>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                    {modifyRight && !circulationDetail.is_recalled ? (
                                                        <Tooltip title="Forward">
                                                            <Fab
                                                                color="primary"
                                                                size="small"
                                                                aria-label="forward"
                                                                className={classes.messageBtn}
                                                                onClick={forwardCirculationDialogHandleOpen}
                                                            >
                                                                <ForwardIcon/>
                                                            </Fab>
                                                        </Tooltip>
                                                    ) : (
                                                        ""
                                                    )}
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        </List>

                                        <Divider className={classes.divider}/>

                                        <div className={classes.messageRecipient}>
                                            <Legend
                                                id="to_recipient"
                                                label="TO"
                                                normalTitleColor={grayColor[10]}
                                                normalBorderColor={grayColor[4]}
                                                messageTitleColor={redColor[6]}
                                                messageBorderColor={redColor[6]}
                                            >
                                                <div className={classes.messageRecipientArea}>
                                                    {circulationDetail.recipients.TO.map((res, index) => {
                                                        return (
                                                            <Fragment key={index}>
                                                                <RecipientChip
                                                                    id={res.group_seq}
                                                                    rcptType={res.rcpt_type}
                                                                    rcptName={res.rcpt_name}
                                                                    actionButton={null}
                                                                    removeAction={null}
                                                                    handleRecipientClipClick={handleRecipientClipClick(
                                                                        res.group_seq
                                                                    )}
                                                                />

                                                                <Popover
                                                                    id={res.rcpt_seq}
                                                                    open={openPopoverId === res.group_seq}
                                                                    anchorEl={anchorEl}
                                                                    onClose={handleRecipientClipClose}
                                                                    anchorOrigin={{
                                                                        vertical: "bottom",
                                                                        horizontal: 115,
                                                                    }}
                                                                    transformOrigin={{
                                                                        vertical: "top",
                                                                        horizontal: "center",
                                                                    }}
                                                                >
                                                                    {res.rcpt_type === "USERGROUP" ? (
                                                                        <div className={classes.popoverMain}>
                                                                            {res.members.map((member) => {
                                                                                return (
                                                                                    <div key={member.site_user_seq}>
                                                                                        <div
                                                                                            className={classes.popoverMember}
                                                                                        >
                                                                                            <PopoverMemberNameTypography>
                                                                                                {member.fullname}
                                                                                            </PopoverMemberNameTypography>
                                                                                            <PopoverCompanyNameTypography>
                                                                                                {member.company_name}
                                                                                            </PopoverCompanyNameTypography>
                                                                                        </div>
                                                                                        <Divider/>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    ) : (
                                                                        ""
                                                                    )}
                                                                </Popover>
                                                            </Fragment>
                                                        );
                                                    })}
                                                </div>
                                            </Legend>
                                        </div>

                                        <div className={classes.messageRecipient}>
                                            <Legend
                                                id="cc_recipient"
                                                label="CC"
                                                normalTitleColor={grayColor[10]}
                                                normalBorderColor={grayColor[4]}
                                                messageTitleColor={redColor[6]}
                                                messageBorderColor={redColor[6]}
                                            >
                                                <div className={classes.messageRecipientArea}>
                                                    {circulationDetail.recipients.CC.map((res, index) => {
                                                        return (
                                                            <Fragment key={index}>
                                                                <RecipientChip
                                                                    id={res.group_seq}
                                                                    rcptType={res.rcpt_type}
                                                                    rcptName={res.rcpt_name}
                                                                    actionButton={null}
                                                                    removeAction={null}
                                                                    handleRecipientClipClick={handleRecipientClipClick(
                                                                        res.group_seq
                                                                    )}
                                                                />

                                                                <Popover
                                                                    id={res.rcpt_seq}
                                                                    open={openPopoverId === res.group_seq}
                                                                    anchorEl={anchorEl}
                                                                    onClose={handleRecipientClipClose}
                                                                    anchorOrigin={{
                                                                        vertical: "bottom",
                                                                        horizontal: 115,
                                                                    }}
                                                                    transformOrigin={{
                                                                        vertical: "top",
                                                                        horizontal: "center",
                                                                    }}
                                                                >
                                                                    {res.rcpt_type === "USERGROUP" ? (
                                                                        <div className={classes.popoverMain}>
                                                                            {res.members.map((member) => {
                                                                                return (
                                                                                    <div key={member.site_user_seq}>
                                                                                        <div
                                                                                            className={classes.popoverMember}
                                                                                        >
                                                                                            <PopoverMemberNameTypography>
                                                                                                {member.fullname}
                                                                                            </PopoverMemberNameTypography>
                                                                                            <PopoverCompanyNameTypography>
                                                                                                {member.company_name}
                                                                                            </PopoverCompanyNameTypography>
                                                                                        </div>
                                                                                        <Divider/>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    ) : (
                                                                        ""
                                                                    )}
                                                                </Popover>
                                                            </Fragment>
                                                        );
                                                    })}
                                                </div>
                                            </Legend>
                                        </div>

                                        <div className={classes.circculationContentArea}>
                                            <div className={classes.masterArea}>
                                                <Paper>
                                                    <List>
                                                        <ListItem>
                                                            <ListItemAvatar>
                                                                <Avatar
                                                                    aria-label="recipe"
                                                                    className={classes.avatar}
                                                                    style={{
                                                                        "--avatar-color": avatarNameColor(
                                                                            circulationDetail.messages[0].sender_fullname.substring(
                                                                                0,
                                                                                1
                                                                            )
                                                                        ),
                                                                    }}
                                                                >
                                                                    {circulationDetail.messages[0].sender_fullname.substring(
                                                                        0,
                                                                        1
                                                                    )}
                                                                </Avatar>
                                                            </ListItemAvatar>

                                                            <ListItemText
                                                                primary={
                                                                    <CircSendInfoTypography variant="body1">
                                                                        {
                                                                            circulationDetail.messages[0]
                                                                                .sender_fullname
                                                                        }
                                                                    </CircSendInfoTypography>
                                                                }
                                                                secondary={
                                                                    circulationDetail.messages[0].send_date
                                                                }
                                                            />

                                                            <ListItemSecondaryAction></ListItemSecondaryAction>
                                                        </ListItem>
                                                    </List>

                                                    <Divider className={classes.divider}/>

                                                    <div className={classes.messageAttachment}>
                                                        <Legend
                                                            id="file_attachment"
                                                            label="ATTACHMENT"
                                                            normalTitleColor={grayColor[10]}
                                                            normalBorderColor={grayColor[4]}
                                                            messageTitleColor={redColor[6]}
                                                            messageBorderColor={redColor[6]}
                                                        >
                                                            <div className={classes.messageAttachmentArea}>
                                                                {circulationDetail !== null
                                                                    ? circulationDetail.messages[0].msg_files.map(
                                                                        (res) => {
                                                                            if (res.hasOwnProperty("file_subject")) {
                                                                                return (
                                                                                    <CirculationAttachmentChip
                                                                                        key={res.msg_file_seq}
                                                                                        id={res.file_seq}
                                                                                        filename={res.filename}
                                                                                        fileSize={res.filesize}
                                                                                        fileExtension={res.extension}
                                                                                        fielRead={res.is_read}
                                                                                        fileSubject={res.file_subject}
                                                                                        // viewFileAction={() => viewFileAction(res.file_cat, res.ref_seq, res.filename)}
                                                                                        // downloadFileAction={() => downloadFileAction(res.file_cat, res.msg_file_seq)}

                                                                                        displayType={"READ"}
                                                                                        viewFileAction={() =>
                                                                                            viewFileAction(
                                                                                                circulationDetail.circ_seq,
                                                                                                circulationDetail.messages[0]
                                                                                                    .msg_seq,
                                                                                                res.msg_file_seq,
                                                                                                res.filename
                                                                                            )
                                                                                        }
                                                                                        downloadFileAction={() =>
                                                                                            downloadFileAction(
                                                                                                circulationDetail.circ_seq,
                                                                                                circulationDetail.messages[0]
                                                                                                    .msg_seq,
                                                                                                res.msg_file_seq
                                                                                            )
                                                                                        }
                                                                                        fileRemoveAction={null}
                                                                                    />
                                                                                );
                                                                            } else {
                                                                                return (
                                                                                    <AttachmentChip
                                                                                        key={res.msg_file_seq}
                                                                                        id={res.msg_file_seq}
                                                                                        filename={res.filename}
                                                                                        fileSize={res.filesize}
                                                                                        fileExtension={res.extension}
                                                                                        viewFileAction={() =>
                                                                                            viewFileAction(
                                                                                                circulationDetail.circ_seq,
                                                                                                circulationDetail.messages[0]
                                                                                                    .msg_seq,
                                                                                                res.msg_file_seq,
                                                                                                res.filename
                                                                                            )
                                                                                        }
                                                                                        downloadFileAction={() =>
                                                                                            downloadFileAction(
                                                                                                circulationDetail.circ_seq,
                                                                                                circulationDetail.messages[0]
                                                                                                    .msg_seq,
                                                                                                res.msg_file_seq
                                                                                            )
                                                                                        }
                                                                                        fileRemoveAction={null}
                                                                                    />
                                                                                );
                                                                            }
                                                                        }
                                                                    )
                                                                    : ""}
                                                            </div>
                                                        </Legend>
                                                    </div>

                                                    <div className={classes.messageContent}>
                                                        <MsgContentTypography component={"span"}>
                                                            {ReactHtmlParser(
                                                                circulationDetail.messages[0].content
                                                            )}
                                                        </MsgContentTypography>
                                                    </div>
                                                </Paper>
                                            </div>

                                            <div className={classes.replyArea}>
                                                {circulationDetail !== null
                                                    ? circulationDetail.messages.map((res, index) => {
                                                        if (index !== 0) {
                                                            return (
                                                                <Paper key={res.msg_seq}>
                                                                    <List>
                                                                        <ListItem>
                                                                            <ListItemAvatar>
                                                                                <Avatar
                                                                                    aria-label="recipe"
                                                                                    className={classes.avatar}
                                                                                    style={{
                                                                                        "--avatar-color": avatarNameColor(
                                                                                            res.sender_fullname.substring(
                                                                                                0,
                                                                                                1
                                                                                            )
                                                                                        ),
                                                                                    }}
                                                                                >
                                                                                    {res.sender_fullname.substring(0, 1)}
                                                                                </Avatar>
                                                                            </ListItemAvatar>

                                                                            <ListItemText
                                                                                primary={
                                                                                    <CircSendInfoTypography>
                                                                                        {res.sender_fullname}
                                                                                    </CircSendInfoTypography>
                                                                                }
                                                                                secondary={res.send_date}
                                                                            />
                                                                        </ListItem>
                                                                    </List>

                                                                    <Divider className={classes.divider}/>

                                                                    {res.msg_files.length > 0 ? (
                                                                        <div className={classes.messageAttachment}>
                                                                            <Legend
                                                                                id="file_attachment"
                                                                                label="ATTACHMENT"
                                                                                normalTitleColor={grayColor[10]}
                                                                                normalBorderColor={grayColor[4]}
                                                                                messageTitleColor={redColor[6]}
                                                                                messageBorderColor={redColor[6]}
                                                                            >
                                                                                <div
                                                                                    className={
                                                                                        classes.messageAttachmentArea
                                                                                    }
                                                                                >
                                                                                    {res.msg_files.map((res) => {
                                                                                        return (
                                                                                            <AttachmentChip
                                                                                                key={res.msg_file_seq}
                                                                                                id={res.msg_file_seq}
                                                                                                filename={res.filename}
                                                                                                fileSize={res.filesize}
                                                                                                fileExtension={res.extension}
                                                                                                viewFileAction={() =>
                                                                                                    viewFileAction(
                                                                                                        res.file_cat,
                                                                                                        res.msg_file_seq
                                                                                                    )
                                                                                                }
                                                                                                downloadFileAction={() =>
                                                                                                    downloadFileAction(
                                                                                                        res.file_cat,
                                                                                                        res.msg_file_seq
                                                                                                    )
                                                                                                }
                                                                                                fileRemoveAction={null}
                                                                                            />
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            </Legend>
                                                                        </div>
                                                                    ) : (
                                                                        ""
                                                                    )}
                                                                    <div className={classes.messageContent}>
                                                                        <MsgContentTypography component={"span"}>
                                                                            {ReactHtmlParser(res.content)}
                                                                        </MsgContentTypography>
                                                                    </div>
                                                                </Paper>
                                                            );
                                                        }
                                                    })
                                                    : ""}
                                            </div>
                                        </div>
                                    </Fragment>
                                ) : (
                                    ""
                                )
                            ) : (
                                ""
                            )}
                        </Grid>
                    </Grid>
                </Fragment>
            )
    );
};

export default Circulation;
